/* eslint-disable @typescript-eslint/no-empty-object-type */
import { format } from 'date-fns';
import { track } from './amplitude';
import { Feilmelding } from './amplitude/ampli';
import { getCookieValue } from './cookie';
import { checkResponseStatus, createQueryString } from './hn-service';
import { warn, error as logError } from './logger';
import resources from './resources/HN.CoreFrontend.NPM.Packages.FrameworkUtils.nb-NO.json';
const isProxyErrorResponse = (response) => {
    return (typeof response === 'object' &&
        response !== null &&
        response.code !== undefined &&
        response.errorCategory !== undefined &&
        response.message !== undefined);
};
const isPascalCaseProxyErrorResponse = (response) => {
    return (typeof response === 'object' &&
        response !== null &&
        response.Code !== undefined &&
        response.ErrorCategory !== undefined &&
        response.Message !== undefined);
};
const mapPascalCaseErrorToCamelCase = (response) => ({
    code: response.Code,
    errorCategory: response.ErrorCategory,
    message: response.Message,
});
const saveCorrelationId = (correlationId) => {
    window.HN = window.HN || {};
    window.HN.CorrelationIdList = window.HN.CorrelationIdList || [];
    window.HN.CorrelationIdList.push(correlationId);
};
/**
 * Hent correlationId fra API-respons
 * @param response Response fra APIet
 * @returns correlationId som guid
 */
export const getCorrelationIdFromResponse = (response) => {
    const correlationId = response.headers.get('CorrelationId');
    if (correlationId) {
        saveCorrelationId(correlationId);
    }
    return correlationId;
};
export const getCorrelationIdList = () => window.HN?.CorrelationIdList;
/**
 * Custom error i tilfelle APIet returnerer en 4XX- eller 5XX-feil, og en standard
 * Helsenorge-feilrespons med code, errorCategory og message.
 *
 * Eksempel på bruk med get():
 *
 * try {
 *   await get('koronasertifikat', 'v1/CoronaCertificate');
 * } catch (error) {
 *   if (error instanceof HelsenorgeProxyError && error.response.code === "HFU-100400") {
 *     // I tilfelle backend svarer med en spesiell feilkode
 *   }
 *   // Gjør noe annet for andre feil
 * }
 */
export class HelsenorgeProxyError extends Error {
    statusCode;
    correlationId;
    response;
    constructor(statusCode, message, correlationId, response, ...params) {
        // Pass remaining arguments (including vendor specific ones) to parent constructor
        super(...params);
        Object.setPrototypeOf(this, new.target.prototype); // restore prototype chain
        // Maintains proper stack trace for where our error was thrown (only available on V8)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, HelsenorgeProxyError);
        }
        this.name = 'HelsenorgeProxyError';
        // Custom debugging information
        this.statusCode = statusCode;
        this.message = message;
        this.correlationId = correlationId;
        this.response = response;
    }
}
/**
 * Custom error i tilfelle APIet returnerer en 4XX- eller 5XX-feil, men der responsen
 * ikke er i henhold til standard.
 */
export class HelsenorgeHttpError extends Error {
    statusCode;
    correlationId;
    response;
    constructor(statusCode, message, correlationId, response, ...params) {
        // Pass remaining arguments (including vendor specific ones) to parent constructor
        super(...params);
        Object.setPrototypeOf(this, new.target.prototype); // restore prototype chain
        // Maintains proper stack trace for where our error was thrown (only available on V8)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, HelsenorgeHttpError);
        }
        this.name = 'HelsenorgeHttpError';
        // Custom debugging information
        this.statusCode = statusCode;
        this.message = message;
        this.correlationId = correlationId;
        this.response = response;
    }
}
/**
 * Returnerer baseUrl til Tjenester basert på HN Rest objektet
 */
export const getTjenesterUrl = () => {
    return window.HN?.Rest?.TjenesterUrl ?? '';
};
/**
 * Returnerer true når window urlen er lik tjenester baseUrl
 */
export const erTjenester = () => {
    if (window && window.location) {
        return window.location.origin === getTjenesterUrl();
    }
    return false;
};
/**
 * Returnerer en full URL til enten et proxy API-endepunkt eller en PDF-link, med MinHelse baseUrl og endepunkt.
 * @param serviceName navn på api-et/løsningsområdet. Eks pasientjournal eller velgbehandlingssted
 * @param endpoint path for endepunktet inkludert versjon. Eks: api/v1/Behandlinger eller v1/Behandlinger
 * @param type angir om det skal genereres en proxy-URL eller en PDF-link. Eks: "proxy" eller "pdf" - Default til proxy.
 */
export const getTjenesterApiUrl = (serviceName, endpoint, type = 'proxy') => {
    const apiUrl = window.HN?.Rest?.TjenesterApiUrl ?? '';
    const base = `${apiUrl}/${type}/${serviceName}`;
    if (endpoint) {
        return `${base}/${endpoint}`;
    }
    return base;
};
export function getServerLogLevel() {
    return window?.HN?.Rest?.LogLevel ?? null;
}
/**
 * Returnerer default params som trengs i en vanlig request
 * @returns Objekt med headers
 */
const getRequestHeaders = () => {
    const headers = {};
    if (window.HN?.Rest?.HendelseLoggType) {
        headers['X-hn-hendelselogg'] = window.HN.Rest.HendelseLoggType;
    }
    if (getCookieValue('HN_CSRF_Token')) {
        headers['X-HN-CSRF-Token'] = getCookieValue('HN_CSRF_Token');
    }
    return headers;
};
/**
 * Returnerer true når window urlen er lik Helsenorge baseUrl
 */
export const erHelsenorge = () => {
    if (window && window.location) {
        return window.location.origin === getHelsenorgeUrl();
    }
    return false;
};
/**
 * Returnerer Headers som trengs i en vanlig request
 * @param type application/json er default
 * @param method HTTP-metoden som skal brukes (valgfritt). Dersom method er 'GET', vil ikke 'Content-Type' settes.
 */
export const createHeaders = (type = 'application/json', method) => {
    const headers = new Headers(getRequestHeaders());
    headers.append('Accept', type);
    if (method?.toLowerCase() !== 'get') {
        headers.append('Content-Type', type);
    }
    return headers;
};
/**
 * Returnerer filnavn fra et Response-objekt ved nedlasting av filer
 * @param response Responsen fra et fetch-kall
 * @returns filnavn fra Content-Disposition-header, eller en fallback dersom headeren ikke finnes
 */
export const getFilenameFromResponse = (response) => {
    let filename = 'nedlasting-' + format(new Date(), 'yyyy-M-d') + '-helseNorge';
    const contentDisposition = response.headers.get('content-disposition');
    if (contentDisposition) {
        const utf8Regex = /filename\*=utf-8''(.+)/i;
        const asciiRegex = /filename="(.+)"/i;
        const asciiRegexWithoutQuotes = /filename=(.+)/i;
        const match = contentDisposition.match(utf8Regex) ??
            contentDisposition.match(asciiRegex) ??
            contentDisposition.match(asciiRegexWithoutQuotes) ??
            false;
        if (match) {
            try {
                filename = decodeURI(match[1]);
            }
            catch {
                logError(`Responsen fra ${response.url} inneholder ugyldige tegn i filnavnet i Content-Disposition-headeren: ${contentDisposition}`);
            }
        }
    }
    return filename;
};
/**
 * Returnerer JSON error objekt - f.eks '{ "Code":"EHAPI-100000", "Message":"Teknisk feil", "ErrorCategory": 0}'
 * @param html ErrorMsg i html
 */
export const getErrorFromHTML = (html) => {
    const first = html.indexOf('{');
    const last = html.lastIndexOf('}');
    const everything = html.substring(first, last + 1);
    if (everything === '') {
        return null;
    }
    return JSON.parse(everything);
};
/**
 * Returnerer url basert på HN Rest objektet
 */
export function getHelsenorgeUrl() {
    return window.HN?.Rest?.HelseNorgeUrl ?? '';
}
/**
 * Sjekker status på en Response og returnerer en Promise avhengig av status
 * @param response Response objektet fra HTTP request
 * @throws {HelsenorgeProxyError} Dersom APIet svarer med en feil etter Helsenorge-standard
 * @throws {HelsenorgeHttpError} Dersom APIet svarer med en feil som ikke er i standard-format
 */
export const checkStatus = (response) => {
    if (response.status === 429) {
        const correlationId = getCorrelationIdFromResponse(response);
        return Promise.reject(new HelsenorgeHttpError(429, 'Denne tjenesten er ikke tilgjengelig for øyeblikket. Det er for mange som ønsker å bruke tjenesten samtidig.', correlationId));
    }
    const mapError = (res, correlationId, body) => {
        if (isProxyErrorResponse(body) &&
            // EHSEC- delen kan fjernes når alle områder er over på Breeze
            (body.code === 'EHSEC-110000' || body.code === 'BZTOK-110000') &&
            document.location?.href.indexOf('autosignout=1') === -1 &&
            document.location?.pathname !== '/autosignout') {
            // redirect dersom token er utgått eller ugyldig
            window.location.href = `${getTjenesterUrl()}/auth/autosignout`;
        }
        if (isProxyErrorResponse(body)) {
            return new HelsenorgeProxyError(res.status, body.message, correlationId, body);
        }
        if (isPascalCaseProxyErrorResponse(body)) {
            return new HelsenorgeProxyError(res.status, body.Message, correlationId, mapPascalCaseErrorToCamelCase(body));
        }
        return new HelsenorgeHttpError(res.status, resources.technical_error_message, correlationId, body);
    };
    return checkResponseStatus(response, mapError);
};
const handleError = (error, apiUrl) => {
    const getFeilkode = () => {
        if (error instanceof HelsenorgeProxyError) {
            return error.response.code || error.statusCode.toString();
        }
        else if (error instanceof HelsenorgeHttpError) {
            return error.statusCode.toString();
        }
        return undefined;
    };
    track(new Feilmelding({
        handling: 'systemevent',
        feiltype: 'nivå 1: teknisk feil',
        feilkode: getFeilkode(),
        feilmelding: 'Feil ved henting av data fra proxy',
    }));
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
        warn(`Kall til følgende URL feilet: ${apiUrl}. Mottok ingen respons fra tjenesten.`);
        throw new Error(resources.technical_error_message);
    }
    throw error;
};
/**
 * baseCrud som brukes for å fetche. Logger eventuelle nettverksfeil med warn().
 * @param method
 * @param proxyName navn på api-et/løsningsområdet. Eks pasientjournal eller velgbehandlingssted
 * @param endpoint  path for endepunktet inkludert versjon. Eks: api/v1/Behandlinger eller v1/Behandlinger
 * @param params parametere som sendes med som query string
 * @param data
 * @throws {Error} Dersom det skjedde en feil under fetch-kallet
 */
const baseCrud = (method, proxyName, endpoint, params, data) => {
    const queryString = params && Object.keys(params).length > 0 ? createQueryString(params) : '';
    const baseUrl = getTjenesterApiUrl(proxyName, endpoint);
    const requestBody = data ? { body: JSON.stringify(data) } : {};
    const apiUrl = baseUrl + queryString;
    return fetch(apiUrl, {
        ...requestBody,
        method,
        credentials: 'include',
        headers: createHeaders('application/json', method),
    })
        .then(response => checkStatus(response))
        .catch(error => handleError(error, apiUrl));
};
/**
 * get method
 * @param proxyName navn på api-et/løsningsområdet. Eks pasientjournal eller velgbehandlingssted
 * @param endpoint  path for endepunktet inkludert versjon. Eks: api/v1/Behandlinger eller v1/Behandlinger
 * @param params parametere som sendes med som query string
 */
export const get = (proxyName, endPoint, params) => {
    return baseCrud('get', proxyName, endPoint, params);
};
/**
 * post method
 * @param proxyName navn på api-et/løsningsområdet. Eks pasientjournal eller velgbehandlingssted
 * @param endpoint  path for endepunktet inkludert versjon. Eks: api/v1/Behandlinger eller v1/Behandlinger
 * @param params parametere som sendes med som query string
 * @param data
 */
export const post = (proxyName, endPoint, data, params) => {
    return baseCrud('post', proxyName, endPoint, params, data);
};
/**
 * put method
 * @param proxyName navn på api-et/løsningsområdet. Eks pasientjournal eller velgbehandlingssted
 * @param endpoint  path for endepunktet inkludert versjon. Eks: api/v1/Behandlinger eller v1/Behandlinger
 * @param params parametere som sendes med som query string
 * @param data
 */
export const put = (proxyName, endpoint, data, params) => {
    return baseCrud('put', proxyName, endpoint, params, data);
};
/**
 * patch method
 * @param proxyName navn på api-et/løsningsområdet. Eks pasientjournal eller velgbehandlingssted
 * @param endpoint  path for endepunktet inkludert versjon. Eks: api/v1/Behandlinger eller v1/Behandlinger
 * @param params parametere som sendes med som query string
 * @param data
 */
export const patch = (proxyName, endpoint, data, params) => {
    return baseCrud('PATCH', proxyName, endpoint, params, data);
};
/**
 * remove method
 * @param proxyName navn på api-et/løsningsområdet. Eks pasientjournal eller velgbehandlingssted
 * @param endpoint  path for endepunktet inkludert versjon. Eks: api/v1/Behandlinger eller v1/Behandlinger
 * @param params parametere som sendes med som query string
 * @param data
 */
export const remove = (proxyName, endpoint, data, params) => {
    return baseCrud('delete', proxyName, endpoint, params, data);
};
/**
 * Konkatenerer url-lenke basert på environment, proxy, url, request params og custom params
 * @param proxyName navn på api-et/løsningsområdet. Eks pasientjournal eller velgbehandlingssted
 * @param endpoint  path for endepunktet inkludert versjon. Eks: api/v1/Behandlinger eller v1/Behandlinger
 * @param params parametere som sendes med som query string
 */
export const link = (proxyName, endpoint, params) => {
    return getTjenesterApiUrl(proxyName, endpoint) + (params ? createQueryString(params) : '');
};
/**
 * Las ned en fil/Blob i nettleseren
 * @param download Info om fil som skal lastes ned
 */
const downloadFileInBrowser = async (download) => {
    const blob = await download.blob;
    const a = document.createElement('a');
    document.body.appendChild(a);
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = download.fileName;
    a.click();
    window.URL.revokeObjectURL(url);
};
/**
 * Sjekker status på en Response og returnerer info om fil som skal nedlastes
 * @param response Response objektet fra HTTP request
 * @throws {HelsenorgeProxyError} Dersom APIet svarer med en feil etter Helsenorge-standard
 * @throws {HelsenorgeHttpError} Dersom APIet svarer med en feil som ikke er i standard-format
 */
const handleDownloadResponse = async (response) => {
    if (!response.ok) {
        const error = await response.text();
        const correlationId = getCorrelationIdFromResponse(response);
        if (error === '401') {
            window.location.reload();
            throw new HelsenorgeHttpError(401, resources.technical_error_message, correlationId, error);
        }
        else {
            logError('responseHtml', error);
            const errorResponse = getErrorFromHTML(error);
            if (isProxyErrorResponse(errorResponse)) {
                throw new HelsenorgeProxyError(response.status, errorResponse.message, correlationId, errorResponse);
            }
            else if (isPascalCaseProxyErrorResponse(errorResponse)) {
                throw new HelsenorgeProxyError(response.status, errorResponse.Message, correlationId, mapPascalCaseErrorToCamelCase(errorResponse));
            }
            throw new HelsenorgeHttpError(response.status, resources.technical_error_message, correlationId, error);
        }
    }
    const fileName = getFilenameFromResponse(response);
    const blob = response.blob();
    return { blob, fileName };
};
/**
 * Fetch for nedlasting av filer
 * @param proxyName navn på api-et/løsningsområdet. Eks pasientjournal eller velgbehandlingssted
 * @param endpoint  path for endepunktet inkludert versjon. Eks: api/v1/Behandlinger eller v1/Behandlinger
 * @param params parametere som sendes med som query string
 * @param method HTTP-metode: get eller post
 * @param data Data som som sendes som body med fetch
 */
export const download = (proxyName, endpoint, params, method = 'get', data) => {
    const queryString = method === 'get' ? createQueryString(params) : '';
    const baseUrl = getTjenesterApiUrl(proxyName, endpoint);
    const requestBody = data ? { body: JSON.stringify(data) } : {};
    const apiUrl = baseUrl + queryString;
    const headers = createHeaders('application/json', method);
    if (method === 'get') {
        headers.set('Content-Type', 'multipart/form-data');
    }
    return new Promise((resolve, reject) => {
        fetch(apiUrl, {
            ...requestBody,
            method,
            credentials: 'include',
            headers,
        })
            .then(handleDownloadResponse)
            .then(downloadFileInBrowser)
            .then(() => resolve())
            .catch(error => {
            track(new Feilmelding({
                handling: 'systemevent',
                feiltype: 'nivå 1: teknisk feil',
                feilkode: error instanceof HelsenorgeProxyError ? error.response.code || error.statusCode.toString() : undefined,
                feilmelding: 'Feil ved nedlasting av fil',
            }));
            if (error instanceof TypeError && error.message === 'Failed to fetch') {
                reject(new Error(resources.technical_error_message));
            }
            reject(error);
        });
    });
};
/**
 * Last opp fil
 * @param proxyName navn på api-et/løsningsområdet. Eks pasientjournal eller velgbehandlingssted
 * @param endpoint  path for endepunktet inkludert versjon. Eks: api/v1/Behandlinger eller v1/Behandlinger
 * @param formData Fil som skal lastes opp
 * @param params parametere som sendes med som query string
 * @throws {Error} Dersom det skjedde en feil under fetch-kallet
 */
export const upload = (proxyName, endpoint, formData, params) => {
    const baseUrl = getTjenesterApiUrl(proxyName, endpoint);
    const queryString = params && Object.keys(params).length > 0 ? createQueryString(params) : '';
    const apiUrl = baseUrl + queryString;
    return fetch(apiUrl, {
        body: formData,
        method: 'POST',
        credentials: 'include',
        headers: new Headers(getRequestHeaders()),
    })
        .then(response => checkStatus(response))
        .catch(error => handleError(error, apiUrl));
};
/**
 * Hent XML fra proxy
 * @param proxyName navn på api-et/løsningsområdet. Eks pasientjournal eller velgbehandlingssted
 * @param endpoint  path for endepunktet inkludert versjon. Eks: api/v1/Behandlinger eller v1/Behandlinger
 * @param params parametere som sendes med som query string
 * @returns XML som string
 */
export const getXml = (proxyName, endpoint, params) => {
    const baseUrl = getTjenesterApiUrl(proxyName, endpoint);
    const apiUrl = baseUrl + createQueryString(params);
    const headers = createHeaders('application/xml', 'GET');
    const checkStatus = (response) => {
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/xml') && response.ok) {
            return response.text();
        }
        return response.text().then(error => {
            const correlationId = getCorrelationIdFromResponse(response);
            throw new HelsenorgeHttpError(response.status, 'Teknisk feil', correlationId, error);
        });
    };
    return fetch(apiUrl, {
        credentials: 'include',
        headers,
    })
        .then(checkStatus)
        .catch(error => handleError(error, apiUrl));
};
